import type { CSSProperties } from 'react';

import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Icon } from '../../../shared/components/icon/icon';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import type { FCWithChildren } from '../../../shared/utils/component.interface';
import { slugify } from '../../../shared/utils/strings';

import styles from './game-frame.module.scss';

const style = createStyleHelper(styles, 'game-frame');

export interface IGameFrameProps {
    launchUrl?: string | null;
    name: string;
    hasError: boolean;
    errorCode?: string;
    className?: string;
    aspectRatio?: {
        ratioX?: number | null;
        ratioY?: number | null;
    } | null;
    onSearch?: () => void;
    showSearchIcon?: boolean;
}

export const GameFrame: FCWithChildren<IGameFrameProps> = ({
    launchUrl,
    hasError,
    errorCode,
    aspectRatio,
    name,
    className,
    children,
    onSearch,
    showSearchIcon = false,
}) => {
    return (
        <div
            className={createClassName([style(), className])}
            style={
                {
                    '--aspect-ratio-x': aspectRatio?.ratioX || 16,
                    '--aspect-ratio-y': aspectRatio?.ratioY || 9,
                } as CSSProperties
            }
        >
            <div className={style('header')}>
                <h1 className={style('title')}>{name}</h1>
                {showSearchIcon ? (
                    <button className={style('search')} onClick={onSearch}>
                        <Icon className={style('search-icon')} name={'search-icon'} />
                    </button>
                ) : null}
            </div>
            <div className={style('wrapper', { 'has-search': showSearchIcon })}>
                {launchUrl && !hasError ? (
                    <iframe className={style('frame')} src={launchUrl}></iframe>
                ) : null}
                {hasError ? (
                    <div className={style('frame', ['error'])}>
                        <Icon name={'error-icon'} className={style('error-icon')} />
                        <span className={style('error')}>
                            <FormattedMessage id={`game-frame.error.${slugify(errorCode)}`} />
                        </span>
                    </div>
                ) : null}
                {children ? <div className={style('content')}>{children}</div> : null}
            </div>
        </div>
    );
};
